import {customElement, property} from 'lit/decorators.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import type {Model, CanvasTarget} from './architecture.js';
import {Msg, main} from './architecture.js';
import CanvasElement, {
  CanvasElementEventAction,
} from '../canvas_element/index.js';
import {execWhenMoved} from './utils.js';
import type CanvasNode from '../canvas_node/index.js';
import styles from './style.scss';
import {dispatchMovedEvent} from '@gl/lit/utils/commands/journey_builder.js';
import {isCanvasNodeType} from '@gl/lit/types/canvas.js';

export const CANVAS_CONNECTION_ELEMENT_TAG = 'fly-canvas-connection';

/**
 * A component for managing edges between nodes on a canvas.
 */
@customElement(CANVAS_CONNECTION_ELEMENT_TAG)
export default class CanvasConnection extends ImmutableComponent<
  Constructor<CanvasElement>,
  Model,
  Msg
>({component: CanvasElement, main}) {
  _canvasType = 'connection';
  @property()
  sourceKey = '';

  @property()
  targetKey = '';

  @property({type: Object})
  source?: CanvasNode;

  @property({type: Object})
  target?: CanvasTarget;

  @property({type: Number})
  connectorIndex = -1;

  delete() {
    this.model.target?.disconnect(this);

    if (isCanvasNodeType(this.model.source)) {
      this.model.source?.detachOutput(this);
    }

    this.dispatchEvent(this.createEvent(CanvasElementEventAction.Destroyed));

    super.delete();
  }

  connectToTarget(target: CanvasTarget) {
    if (!target || target.isSameNode(this.model.source)) return;

    target.unfocus();
    target.connect(this);

    this.handleUpdate(Msg.setTarget(target));

    execWhenMoved(target, () => {
      this.handleUpdate(Msg.setEndBound(target.boundingBox));
    });
  }

  rendered() {
    super.rendered();
    if (this.source) dispatchMovedEvent(this.source);
    if (this.target) dispatchMovedEvent(this.target);
  }

  static get styles() {
    return [styles];
  }

  get canvasType() {
    return this._canvasType;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CANVAS_CONNECTION_ELEMENT_TAG]: CanvasConnection;
  }
}
